import {
  capitalize,
  Stack
} from "@mui/material";
import { Spinner } from "UI/Spinner";
import { ReactComponent as RSLogo } from "images/rs-logo.svg";
import { ReactComponent as SuccessIcon } from "images/success.svg";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as FP from "utils/fp-js";
import { Container, Header } from "../../Dashboard.style";
import { ErrorWindow } from "../ErrorWindow";
import { ActionSelectorForm } from "./ActionSelectorForm";
import { PatientDetailsForm } from "./PatientDetailsForm";
import { QuestionForm } from "./QuestionForm";
import {
  defaultValues,
  toastifyConfig,
  types
} from "./ReferralForm.common";
import { format } from "date-fns";

export const PublicReferralForm = () => {
  const navigate = useNavigate();
  const { formCode } = useParams();
  const [prevValues, setPrevValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState();
  const [step, setStep] = useState(types.Notification);
  const [isCodeWrong, setIsCodeWrong] = useState(false);

  const { control, setValue, getValues, reset, watch, trigger } = useForm({
    defaultValues,
  });

  const handleResetAndBack = () => {
    setStep(types.Notification);
    reset(prevValues);
  };

  const showSuccessNotification = () =>
    toast(
      () => (
        <Stack direction={"row"} gap={1}>
          <SuccessIcon />
          <Stack>
            {getValues()?.type} sent.
            <div className="text-sm">
              Thank you for reaching out to {recipientName}. They will contact
              you shortly.
            </div>
          </Stack>
        </Stack>
      ),
      toastifyConfig
    );

    const onQuestionSubmit = async (newValues) => {
    const values = newValues?.type === "Referral" ? newValues : getValues();

    if (values?.patientDob) {
      values.patientDob = format(new Date(values.patientDob), "yyyy-MM-dd");
    }
  
    const formData = new FormData();

    for (const key of  Object.keys(values)) {
      if (Array.isArray(values[key])) {
        values[key].forEach((item) => {
          formData.append(`${key}[]`, JSON.stringify(item));
        });
      } else {
        formData.append(key, values[key]);
      }
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notification_update`,
        {
          method: "POST",
          body: formData,
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setError(
          capitalize(result?.message ?? result?.error ?? "Something went wrong")
        );
        return;
      }

      showSuccessNotification();
      navigate("/");
    } catch (error) {
      setError(
        capitalize(error?.message ?? error?.error ?? "Something went wrong")
      );
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/public/referral`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code: formCode }),
        }
      );
      const result = await response.json();
      if (response?.status !== 200 || result?.error || !result?.referral) {
        setIsCodeWrong(true);
        return;
      }

      const referral = result.referral;
      const newData = {
        ...defaultValues,
        id: referral?.id,
        status: referral?.status,
        originatorName: referral?.originatorName,
        originatorEmail: referral?.originatorEmail,
        originatorPhone: referral?.originatorPhone,
        recipientName: referral?.recipientName,
        buttonType: referral?.buttonType,
        type: referral?.type,
        oneTimeCode: referral?.oneTimeCode,
      };
      setPrevValues(newData);
      reset(newData);
      setProviders(result?.referral?.providers ?? []);
    } catch (error) {
      setError(
        capitalize(error?.message ?? error?.error ?? "Something went wrong")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const type = watch("type");
  const isButtonEnabled = type !== types.Notification;
  const recipientName = watch("recipientName");
  const { originatorName, originatorEmail, originatorPhone } = getValues();
  const sender = [originatorName, originatorEmail, originatorPhone]
    .filter(Boolean)
    .join(", ");
  const provider = providers?.reduce(
    (acc, { name, speciality }) =>
      [acc, [name, speciality].filter(Boolean).join(", ")]
        .filter(Boolean)
        .join("; "),
    ""
  );
  const currentYear = new Date().getFullYear();

  return (
    <Container>
      <Header>
        <RSLogo width={"103px"} />
      </Header>
      <div
        className={[
          "flex flex-col xl:flex-row 2xl:flex-row xl:justify-between 2xl:justify-between",
          "bg-gray-100 px-2 py-4 flex-1",
        ].join(" ")}
      >
        {isCodeWrong && (
          <div className="flex-1 flex flex-col justify-center items-center pb-60 font-bold text-lg">
            Looks like this link is wrong or expired.
          </div>
        )}
        {!isCodeWrong &&
          !!prevValues &&
          FP.match(step)
            .on(
              types.Referral,
              <PatientDetailsForm
                control={control}
                watch={watch}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                reset={reset}
                setError={setError}
                loading={loading}
                setLoading={setLoading}
                isButtonEnabled={isButtonEnabled}
                onQuestionSubmit={onQuestionSubmit}
                handleResetAndBack={handleResetAndBack}
                recipientName={recipientName}
                provider={provider}
                sender={sender}
              />
            )
            .on(
              types.Question,
              <QuestionForm
                handleResetAndBack={handleResetAndBack}
                recipientName={recipientName}
                control={control}
                watch={watch}
                isButtonEnabled={isButtonEnabled}
                loading={loading}
                onQuestionSubmit={onQuestionSubmit}
                provider={provider}
                sender={sender}
              />
            )
            .otherwise(
              <ActionSelectorForm
                recipientName={recipientName}
                provider={provider}
                sender={sender}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                isButtonEnabled={isButtonEnabled}
                setStep={setStep}
              />
            )}
      </div>
      <div className="mb-4 text-gray-500 text-sm flex-grow-0 flex-shrink-0 text-center">
        Copyright © {currentYear} Referral Stream. All rights reserved.
      </div>
      {loading && <Spinner />}
      {error && <ErrorWindow error={error} handleClose={() => setError("")} />}
    </Container>
  );
};
